// Copyright (c) Bentley Systems
import { useId, useState } from "react";
import { BaseFilter, Button, Radio } from "@itwin/itwinui-react";
import type { TableFilterProps } from "@itwin/itwinui-react/react-table";

export type TableChoiceFilterOption = {
  value: string;
  label: string;
};

/**
 * Creates a table column filter that offers a fixed set of choices as radio buttons, for columns
 * that are not filtered by text (the page turns the chosen value into its own query).
 *
 * Uses the same iTwinUI primitives and Filter / Clear bar as `ODataTextFilter`, so it looks like
 * the other column filters.
 */
export const choiceFilter = (
  options: readonly TableChoiceFilterOption[],
  label: string,
) => {
  const ChoiceFilter = <T extends Record<string, unknown>>(
    props: TableFilterProps<T>,
  ) => {
    const { column, setFilter, clearFilter } = props;
    const [choice, setChoice] = useState<string | undefined>(
      column.filterValue as string | undefined,
    );
    const name = useId();

    return (
      <BaseFilter
        onSubmit={() => {
          if (choice) {
            setFilter(choice);
          }
        }}
      >
        <div role="radiogroup" aria-label={label} className="becs-filter-choices">
          {options.map((option) => (
            <Radio
              key={option.value}
              name={name}
              value={option.value}
              label={option.label}
              checked={choice === option.value}
              onChange={() => setChoice(option.value)}
            />
          ))}
        </div>
        <div className="becs-filter-button-bar">
          <Button type="submit" styleType="high-visibility" disabled={!choice}>
            Filter
          </Button>
          <Button onClick={clearFilter}>Clear</Button>
        </div>
      </BaseFilter>
    );
  };
  return ChoiceFilter;
};
